// Below this many full wallets' worth, the exposure cap is probably too low;
// above the upper bound, it's probably too high.
const MIN_WALLETS_PER_EXPOSURE = 500;
const MAX_WALLETS_PER_EXPOSURE = 2500;

// Ontario restricts expiring reward points once a balance can be worth more
// than this, and other provinces have similar rules.
const CANADA_BALANCE_NOTE_THRESHOLD = 50;

/**
 * Warn when the maximum exposure is out of proportion to the maximum balance.
 *
 * Returns a message when the exposure would fill fewer than 500 or more than
 * 2,500 wallets, and null when it's in range or either limit is blank.
 */
export const getExposureWarning = (
  maxExposure: number | null | undefined,
  maxAccountBalance: number | null | undefined,
): string | null => {
  if (!maxExposure || !maxAccountBalance) {
    return null;
  }
  const ratio = maxExposure / maxAccountBalance;
  const customersCount = Math.round(ratio).toLocaleString();
  if (ratio < MIN_WALLETS_PER_EXPOSURE) {
    return `That’s only enough for about ${customersCount} climbers to fill their wallets. Check this isn’t too low.`;
  }
  if (ratio > MAX_WALLETS_PER_EXPOSURE) {
    return `That’s enough for about ${customersCount} climbers to fill their wallets. Check this isn’t too high.`;
  }
  return null;
};

/**
 * Whether to show Canadian gyms a note about provincial rules on expiring credits.
 *
 * True when a climber could hold more than $50 of credits: either the maximum
 * balance is worth more than that, or it's blank, which means no limit.
 */
export const shouldShowCanadaBalanceNote = (
  country: string | undefined,
  maxAccountBalance: number | null | undefined,
  creditValue: number | null | undefined,
): boolean => {
  if (country !== "CA") {
    return false;
  }
  // A blank balance means no limit, so a climber's credits can be worth any amount.
  if (maxAccountBalance === null || maxAccountBalance === undefined) {
    return true;
  }
  if (!creditValue) {
    return false;
  }
  return maxAccountBalance * creditValue > CANADA_BALANCE_NOTE_THRESHOLD;
};
